import React from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import Dropdown from 'react-bootstrap/Dropdown';

import CustomToggle from './CustomToggle';

import './styles.scss';
import { stopPropagation } from 'utils/helper';

const DropDownMenu = ({
  toggleLabel,
  toggleIcon,
  className,
  toggleClass,
  menuClass,
  menuOptions,
  selectedOption,
  onChange,
  menuPortal,
  renderMenuItemContent,
  suppressMenuItemTitle,
  ...rest
}) => {
  const handleOnchange = (eventKey, event) => {
    stopPropagation(event);
    onChange(eventKey);
  };

  const handleMenuItemClick = menu => event => {
    if (menu?.onClick) {
      menu.onClick(menu.value, event);
    }
  };

  const getMenuTitle = label => {
    return typeof label === 'string'
      ? label.replace(/\b\w/g, char => char.toUpperCase())
      : undefined;
  };

  const renderDefaultMenuItemContent = menu => {
    const label =
      typeof menu?.label === 'string' && menu?.label?.length > 44
        ? menu?.label?.slice(0, 44)?.concat('..')
        : menu?.label;

    return (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {menu?.icon && <span style={{ marginRight: '8px' }}>{menu.icon}</span>}
        {label}
      </div>
    );
  };

  const dropdownMenu = (
    <Dropdown.Menu className={`app-dropdown-menu ${menuClass}`}>
      {menuOptions?.map((menu, index) =>
        menu?.value === 'divider' ? (
          <Dropdown.Divider key={`${menu.value}-index`} />
        ) : (
          <Dropdown.Item
            key={`${menu.value}-index`}
            eventKey={menu.value}
            as="div"
            active={selectedOption === menu.value}
            title={
              suppressMenuItemTitle ? undefined : getMenuTitle(menu?.label)
            }
            onClick={handleMenuItemClick(menu)}
          >
            {renderMenuItemContent
              ? renderMenuItemContent(menu, index)
              : renderDefaultMenuItemContent(menu)}
          </Dropdown.Item>
        )
      )}
    </Dropdown.Menu>
  );

  return (
    <Dropdown
      className={`app-dropdown ${className}`}
      onSelect={handleOnchange}
      {...rest}
    >
      <Dropdown.Toggle
        as={CustomToggle}
        toggleClassName={toggleClass}
        toggleIcon={toggleIcon}
      >
        {toggleLabel}
      </Dropdown.Toggle>
      {menuPortal && typeof document !== 'undefined'
        ? createPortal(dropdownMenu, document.body)
        : dropdownMenu}
    </Dropdown>
  );
};

DropDownMenu.propTypes = {
  className: PropTypes.string,
  toggleClass: PropTypes.string,
  toggleIcon: PropTypes.node,
  menuClass: PropTypes.string,
  toggleLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  menuOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
      onClick: PropTypes.func,
    })
  ).isRequired,
  selectedOption: PropTypes.string,
  onChange: PropTypes.func,
  menuPortal: PropTypes.bool,
  renderMenuItemContent: PropTypes.func,
  suppressMenuItemTitle: PropTypes.bool,
};

DropDownMenu.defaultProps = {
  toggleIcon: null,
  className: '',
  toggleClass: '',
  menuClass: '',
  toggleLabel: '',
  selectedOption: '',
  onChange: () => null,
  menuPortal: false,
  renderMenuItemContent: null,
  suppressMenuItemTitle: false,
};

export default DropDownMenu;
